import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileClock, Pencil, Save, X } from "lucide-react";
import toast from "react-hot-toast";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { hasRequiredRole } from "@/lib/adminRoles";
import { resolveMissionTimesheetFallbackDays } from "@/lib/businessRules/timesheets";
import {
  listMissions,
  listClients,
  listCollaborators,
  listTimesheets,
  createTimesheet,
  updateTimesheet,
} from "@/lib/pilotageApi";
import { effectiveDays } from "@/lib/businessRules/timesheets";
import { emitTimesheetUpdated } from "@/lib/dataEvents";
import {
  getTimesheetMonthStatusLabel,
  isCollaboratorActiveForMonth,
  isMonthWithinMissionWindow,
} from "@/lib/timesheetRules";
import { formatMonthLabel, resolveMissionTypeWithCollaborator } from "@/lib/pilotageUi";

const getCurrentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const EMPTY_EDIT_VALUES = { days_declared: "", days_forecast: "", client_free_days: "" };

const parseNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const toInputValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const hasDailyEntries = (timesheet) =>
  Boolean(timesheet?.daily_entries && Object.keys(timesheet.daily_entries).length > 0);

const getFallbackForecastDays = (mission) => resolveMissionTimesheetFallbackDays(mission);

const getDisplayedForecastDays = (mission, timesheet) =>
  timesheet?.days_forecast ?? getFallbackForecastDays(mission);

const TimesheetsMensuelsPage = () => {
  const { role } = useAuth();
  const canEditTimesheets = hasRequiredRole(role, ["ADMIN"]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState("");

  const urlMonth = searchParams.get("month");
  const selectedMonth = urlMonth || getCurrentMonthKey();
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState(EMPTY_EDIT_VALUES);
  const editableFieldLabel = "Jours déclarés";

  const updateSelectedMonth = (nextMonth) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextMonth) {
      nextSearchParams.set("month", nextMonth);
    } else {
      nextSearchParams.delete("month");
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const loadDataForMonth = async (monthKey) => {
    setLoading(true);
    setError("");

    try {
      const [missionsData, clientsData, collaboratorsData, timesheetsData] = await Promise.all([
        listMissions(),
        listClients(),
        listCollaborators(),
        listTimesheets({ month: monthKey }),
      ]);

      setMissions(missionsData);
      setClients(clientsData);
      setCollaborators(collaboratorsData);
      setTimesheets(timesheetsData);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de charger les données du mois."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditingRow(null);
    setEditValues(EMPTY_EDIT_VALUES);
    void loadDataForMonth(selectedMonth);

    if (urlMonth !== selectedMonth) {
      updateSelectedMonth(selectedMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const activeMissions = useMemo(() => {
    const collaboratorMap = new Map(
      collaborators.map((collaborator) => [collaborator.id, collaborator]),
    );
    return missions.filter((mission) => {
      const collaborator = collaboratorMap.get(mission.collaborator_id);
      return (
        isMonthWithinMissionWindow(mission, selectedMonth) &&
        (!collaborator || isCollaboratorActiveForMonth(collaborator, selectedMonth))
      );
    });
  }, [collaborators, missions, selectedMonth]);

  const clientsById = useMemo(
    () => new Map(clients.map((client) => [client.id, client])),
    [clients],
  );

  const collaboratorsById = useMemo(
    () => new Map(collaborators.map((collaborator) => [collaborator.id, collaborator])),
    [collaborators],
  );

  const timesheetsByMissionId = useMemo(
    () => new Map(timesheets.map((timesheet) => [timesheet.mission_id, timesheet])),
    [timesheets],
  );

  const tableRows = useMemo(
    () =>
      activeMissions.map((mission) => {
        const timesheet = timesheetsByMissionId.get(mission.id);
        const collaborator = collaboratorsById.get(mission.collaborator_id);
        const client = clientsById.get(mission.client_id);
        const missionType = resolveMissionTypeWithCollaborator(mission, collaborator);
        const forecastDays = getDisplayedForecastDays(mission, timesheet);

        return {
          id: mission.id,
          mission,
          timesheet,
          collaborator,
          client,
          missionLabel: mission.mission_role || `Mission ${mission.id}`,
          missionType,
          clientLabel: client?.name || mission.client_id,
          collaboratorLabel: collaborator?.full_name || mission.collaborator_id,
          monthKey: selectedMonth,
          monthLabel: formatMonthLabel(selectedMonth),
          daysDeclared: timesheet?.days_declared ?? null,
          daysForecast: forecastDays,
          clientFreeDays: timesheet?.client_free_days ?? 0,
          daysBilled: timesheet?.days_billed ?? (timesheet?.days_declared != null ? Math.max(0, timesheet.days_declared - (timesheet?.client_free_days || 0)) : null),
          freeDaysGranted: mission?.client_free_days_granted ?? 0,
          freeDaysRemaining: mission?.client_free_days_remaining ?? 0,
          effectiveValue: effectiveDays(timesheet?.days_declared, forecastDays),
          effectiveSource:
            timesheet?.days_declared != null
              ? "Déclaré"
              : (timesheet?.days_forecast != null ? "Prévision ts" : "Prévision mission"),
          locked: Boolean(timesheet?.locked),
          hasDailyEntries: hasDailyEntries(timesheet),
        };
      }),
    [activeMissions, clientsById, collaboratorsById, selectedMonth, timesheetsByMissionId],
  );

  const getActionHint = (row, isEditing = false) => {
    if (row.hasDailyEntries) {
      return "La saisie journalière pilote déjà ce total. Utilisez la mission pour le modifier.";
    }

    if (row.locked) {
      return isEditing
        ? `Override admin actif: ${editableFieldLabel.toLowerCase()} modifiables malgré la clôture.`
        : "Mois clôturé. L'admin peut ouvrir une modification explicite depuis cette ligne.";
    }

    return "Saisissez les jours à déclarer.";
  };

  const handleEdit = (mission, timesheet) => {
    setEditingRow(mission.id);
    setEditValues({
      days_declared: toInputValue(timesheet?.days_declared),
      days_forecast: toInputValue(getDisplayedForecastDays(mission, timesheet)),
      client_free_days: toInputValue(timesheet?.client_free_days),
    });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditValues(EMPTY_EDIT_VALUES);
  };

  const handleSave = async (mission, timesheet) => {
    const fallbackForecastDays = getFallbackForecastDays(mission);
    const resolvedForecastDays = timesheet?.id
      ? timesheet.days_forecast
      : fallbackForecastDays;

    const mutationPayload = {
      days_declared: parseNumber(editValues.days_declared),
    };
    if (editValues.client_free_days !== "") {
      mutationPayload.client_free_days = parseNumber(editValues.client_free_days) ?? 0;
    }
    if (resolvedForecastDays !== undefined && resolvedForecastDays !== null) {
      mutationPayload.days_forecast = Number(resolvedForecastDays);
    }

    setSaving((prev) => ({ ...prev, [mission.id]: true }));
    setError("");

    try {
      if (timesheet?.id) {
        await updateTimesheet(timesheet.id, mutationPayload);
        toast.success("Timesheet mis à jour");
      } else {
        await createTimesheet({
          mission_id: mission.id,
          month: selectedMonth,
          ...mutationPayload,
        });
        toast.success("Timesheet créé");
      }
      setEditingRow(null);
      setEditValues(EMPTY_EDIT_VALUES);
      emitTimesheetUpdated({
        collaboratorId: String(mission?.collaborator_id || ""),
        missionId: String(mission?.id || ""),
        monthKey: selectedMonth,
      });
      // reload both timesheets and missions to keep remaining free days balance updated
      const [updatedTimesheets, updatedMissions] = await Promise.all([
        listTimesheets({ month: selectedMonth }),
        listMissions(),
      ]);
      setTimesheets(updatedTimesheets);
      setMissions(updatedMissions);
    } catch (reqError) {
      const message = getApiErrorMessage(reqError, "Erreur de sauvegarde.");
      setError(message);
      toast.error(message);
    } finally {
      setSaving((prev) => ({ ...prev, [mission.id]: false }));
    }
  };

  const columns = [

    {
      key: "client",
      label: "Client",
      render: (row) => {
        return (
          <div>
            <p className="font-medium text-white">{row.clientLabel}</p>
          </div>
        );
      },
    },
    {
      key: "collaborator",
      label: "Collaborateur",
      render: (row) => {
        return (
          <div>
            <p className="font-medium text-white">{row.collaboratorLabel}</p>
            <p className="mt-1 text-xs text-slate-500">
              {row.collaborator?.contract_type || row.collaborator?.contract_kind || "Contrat non renseigné"}
            </p>
            <div className="text-xs text-slate-500 mt-1">
              ID: {row.mission.id}
            </div>
          </div>
        );
      },
    },

    {
      key: "days_declared",
      label: "Jours travaillés",
      render: (row) => {
        const isEditing = editingRow === row.id;

        if (isEditing) {
          return (
            <Input
              type="number"
              step="0.5"
              min="0"
              data-testid={`timesheet-days-declared-input-${row.id}`}
              className="h-9 w-20 border-white/10 bg-slate-950 text-white"
              value={editValues.days_declared}
              onChange={e => setEditValues({ ...editValues, days_declared: e.target.value })}
              placeholder="-"
            />
          );
        }

        const declaredDays = row.daysDeclared;
        return (
          <span
            className={`text-sm font-semibold ${
              declaredDays !== null && declaredDays !== undefined ? "text-lenvia-blue" : "text-slate-500"
            }`}
          >
            {declaredDays !== null && declaredDays !== undefined ? `${declaredDays} j` : "-"}
          </span>
        );
      },
    },
    {
      key: "client_free_days",
      label: "Gratuité client",
      render: (row) => {
        const hasFreeDaysConfig = row.freeDaysGranted > 0 || row.clientFreeDays > 0;
        if (!hasFreeDaysConfig) {
          return <span className="text-slate-600 text-xs">—</span>;
        }

        const isEditing = editingRow === row.id;
        const availableBalance = Math.max(0, (row.freeDaysRemaining || 0) + (row.clientFreeDays || 0));

        if (isEditing) {
          if (row.freeDaysRemaining === 0 && Number(row.clientFreeDays || 0) === 0) {
            return <span className="text-xs text-slate-500 italic">0 j dispo</span>;
          }

          return (
            <div className="space-y-1">
              <Input
                type="number"
                step="0.5"
                min="0"
                max={Math.min(parseNumber(editValues.days_declared) || 0, availableBalance)}
                data-testid={`timesheet-free-days-input-${row.id}`}
                className="h-9 w-20 border-white/10 bg-slate-950 text-white"
                value={editValues.client_free_days}
                onChange={e => setEditValues({ ...editValues, client_free_days: e.target.value })}
                placeholder="0"
              />
              <p className="text-[10px] text-slate-500">Max {availableBalance} j</p>
            </div>
          );
        }

        if (row.clientFreeDays > 0) {
          return (
            <div>
              <span className="text-sm font-semibold text-amber-400">{row.clientFreeDays} j</span>
              <span className="block text-[10px] text-slate-500">
                {row.freeDaysRemaining} j restant{row.freeDaysRemaining > 1 ? "s" : ""}
              </span>
            </div>
          );
        }

        return (
          <span className="text-xs text-slate-500">
            0 j ({row.freeDaysRemaining} dispo)
          </span>
        );
      },
    },
    {
      key: "days_billed",
      label: "Jours facturables",
      render: (row) => {
        if (row.daysBilled === null || row.daysBilled === undefined) {
          return <span className="text-slate-500 text-xs">—</span>;
        }

        const free = row.clientFreeDays || 0;
        return (
          <div>
            <span className="text-sm font-bold text-white">{row.daysBilled} j</span>
            {free > 0 ? (
              <>
                <span className="block text-[10px] text-amber-400 font-normal">
                  (-{free} j gratuit{free > 1 ? "s" : ""})
                </span>
                <span className="block text-[10px] text-slate-400">
                  sur {row.daysDeclared} j travaillés
                </span>
              </>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "days_forecast",
      label: "Jours prévisionnels",
      render: (row) => {
        return (
          <div>
            <span className="text-sm text-slate-300">{`${row.daysForecast} j`}</span>
            <p className="mt-1 text-[11px] text-slate-500">
              {row.timesheet?.days_forecast != null ? "Source timesheet" : "Fallback mission"}
            </p>
          </div>
        );
      },
    },
    {
      key: "effective",
      label: "Valeur retenue",
      render: (row) => {
        return (
          <div>
            <span className="text-sm font-bold text-white block">
              {row.effectiveValue} j
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {row.effectiveSource}
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "État",
      render: (row) => {
        const isEditingLockedRow = editingRow === row.id && row.locked;

        return (
          <div>
            <span className="text-sm font-medium text-white">
              {getTimesheetMonthStatusLabel({
                monthKey: row.monthKey,
                locked: row.locked,
              })}
            </span>
            {row.hasDailyEntries ? (
              <p className="mt-1 text-[11px] text-amber-300">
                Saisie journalière détectée
              </p>
            ) : null}
            {row.locked ? (
              <p className="mt-1 text-[11px] text-amber-300">
                {isEditingLockedRow ? "Override admin actif" : "Override admin disponible"}
              </p>
            ) : null}
            {isEditingLockedRow ? (
              <p className="mt-1 text-[11px] text-amber-300">
                Modification explicite d&apos;un mois clôturé
              </p>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Timesheets Mensuels — {formatMonthLabel(selectedMonth)}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Pilotez les états de production pour chaque mois. Consultez les missions actives
              et ajustez les jours déclarés ou prévisionnels en direct.
            </p>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Utilisez <span className="font-medium text-slate-200">Modifier</span> sur une ligne.
              Pour un mois passé ou courant, vous éditez les jours déclarés. Pour un mois futur,
              vous éditez les jours prévisionnels.
            </p>
          </div>
          <div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => updateSelectedMonth(e.target.value)}
              className="h-10 rounded-lg border border-white/10 bg-slate-900/50 px-4 text-sm font-semibold text-white shadow-inner focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {!loading && activeMissions.length === 0 ? (
        <PilotageEmptyState
          icon={FileClock}
          title="Aucune mission active"
          description={`Aucune mission n'est active pour ${selectedMonth}. Modifiez le mois pour consulter les timesheets.`}
          actionLabel="Afficher le mois en cours"
          onAction={() => updateSelectedMonth(getCurrentMonthKey())}
        />
      ) : (
        <PilotageTable
          columns={columns}
          rows={tableRows}
          loading={loading}
          emptyMessage={`Aucune mission trouvée.`}
          renderActions={(row) => {
            if (!canEditTimesheets) {
              return null;
            }

            const isEditing = editingRow === row.id;
            const isSaving = Boolean(saving[row.id]);
            const canSave = editValues.days_declared !== "" || Boolean(row.timesheet?.id);

            if (row.hasDailyEntries) {
              return (
                <div className="flex flex-col items-end gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-8 border-white/10 hover:bg-white/5"
                  >
                    <Link to={`/admin/pilotage/missions/${row.id}`}>Voir mission</Link>
                  </Button>
                  <p className="max-w-[16rem] text-right text-[11px] text-slate-500">
                    {getActionHint(row)}
                  </p>
                </div>
              );
            }

            if (isEditing) {
              return (
                <div className="flex flex-col items-end gap-2">
                  <p className="max-w-[16rem] text-right text-[11px] text-slate-500">
                    {getActionHint(row, true)}
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      aria-label="Annuler"
                      className="h-8 border-slate-700 bg-slate-800 px-2 hover:bg-slate-700"
                      title="Annuler"
                    >
                      <X className="h-4 w-4 text-slate-300" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSave(row.mission, row.timesheet)}
                      disabled={isSaving || !canSave}
                      aria-label="Enregistrer"
                      className="h-8 bg-lenvia-blue px-2 hover:bg-blue-600"
                      title="Enregistrer"
                    >
                      {isSaving ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                      ) : (
                        <Save className="h-4 w-4 text-white" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="flex flex-col items-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(row.mission, row.timesheet)}
                  className="h-8 gap-2 border-white/10 hover:bg-white/5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Modifier
                </Button>
                <p className="max-w-[16rem] text-right text-[11px] text-slate-500">
                  {getActionHint(row)}
                </p>
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default TimesheetsMensuelsPage;
